import { CalendarMode } from '@/helpers/calendarMode';
import { formatHebrewBoardDate, jerusalemCivilParts, toJerusalemCivilIso, toJerusalemInstant } from '@/helpers/hebrewDateHelper';
import { transactionFullDateFormat, transactionShortDateFormat } from '@/helpers/dateHelper';

export interface DualCalendarDateProps {
  value: unknown;
  mode: CalendarMode;
  missingLabel: string;
}

/**
 * Variant B dual date: Gregorian primary + Hebrew secondary, or the reverse
 * when the board is in Hebrew-primary mode. Missing dates stay a placeholder.
 */
const DualCalendarDate = ({ value, mode, missingLabel }: DualCalendarDateProps) => {
  // Suffixless ISO must be UTC (israelCalendarDate / hebrewDateHelper), not parseTransactionDate's local parse.
  const date = toJerusalemInstant(value);
  if (!date) {
    return (
      <time data-transaction-date="" data-missing="true" data-calendar-primary={mode} title={missingLabel} aria-label={missingLabel}>
        <span className="g" dir="ltr" aria-hidden="true" data-date-day-month data-date-role="primary">
          —
        </span>
        <span className="h" dir="rtl" aria-hidden="true" data-date-hebrew data-date-role="secondary">
          {missingLabel}
        </span>
      </time>
    );
  }

  const iso = toJerusalemCivilIso(date) ?? jerusalemCivilParts(date).iso;
  const hebrewDate = formatHebrewBoardDate(date);
  const gregorianFull = transactionFullDateFormat.format(date);
  const gregorianShort = transactionShortDateFormat.format(date);
  const year = String(jerusalemCivilParts(date).year);
  const hebrewPrimary = mode === 'hebrew';
  const label = [gregorianFull, hebrewDate].filter(Boolean).join(', ');

  const gregorianLine = (
    <span className="g" dir="ltr" aria-hidden="true" data-date-day-month data-date-role={hebrewPrimary ? 'secondary' : 'primary'}>
      {gregorianShort}
      {!hebrewPrimary && (
        <span className="y" dir="ltr" data-date-year>
          {year}
        </span>
      )}
    </span>
  );

  const hebrewLine = hebrewDate ? (
    <span className="h" dir="rtl" aria-hidden="true" data-date-hebrew data-date-role={hebrewPrimary ? 'primary' : 'secondary'} title={hebrewDate}>
      {hebrewDate}
    </span>
  ) : null;

  return (
    <time dateTime={iso} title={label} aria-label={label} data-transaction-date="" data-calendar-primary={mode}>
      {hebrewPrimary ? (
        <>
          {hebrewLine}
          {gregorianLine}
        </>
      ) : (
        <>
          {gregorianLine}
          {hebrewLine}
        </>
      )}
    </time>
  );
};

export default DualCalendarDate;
